<template>
  <div class="container-fluid py-4">
    <div class="card">
      <div class="card-header bg-light">
        <h5 class="card-title mb-0">Логи</h5>
      </div>
      <div class="card-body">
        <!-- Filters Row -->
        <div class="row g-3 mb-4">
          <div class="col-md-3">
            <label class="form-label">Дата</label>
            <select
                v-model="filters.date"
                @change="updateFilter('date', $event.target.value)"
                class="form-select form-select-sm"
            >
              <option value="">Все даты</option>
              <option v-for="date in availableDates" :key="date" :value="date">
                {{ date }}
              </option>
            </select>
          </div>

          <div class="col-md-2">
            <label class="form-label">Тип</label>
            <select
                v-model="filters.level"
                @change="updateFilter('level', $event.target.value)"
                class="form-select form-select-sm"
            >
              <option value="">Все</option>
              <option value="error">Ошибки</option>
              <option value="warn">Предупреждения</option>
              <option value="info">Информация</option>
            </select>
          </div>

          <div class="col-md-4">
            <label class="form-label">Поиск</label>
            <div class="input-group input-group-sm">
              <input
                  v-model="filters.search"
                  @input="debouncedSearch"
                  placeholder="Поиск в логах..."
                  class="form-control"
              >
              <button class="btn btn-outline-secondary" type="button" @click="clearSearch">
                <i class="bi bi-x"></i>
              </button>
            </div>
          </div>

          <div class="col-md-2">
            <label class="form-label">Лимит</label>
            <select
                v-model="filters.limit"
                @change="updateFilter('limit', $event.target.value)"
                class="form-select form-select-sm"
            >
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="500">500</option>
            </select>
          </div>

          <div class="col-md-1 d-flex align-items-end">
            <button
                class="btn btn-sm btn-primary w-100"
                @click="fetchLogs"
                :disabled="isLoading"
            >
              <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
              {{ isLoading ? 'Загрузка...' : 'Обновить' }}
            </button>
          </div>
        </div>

        <!-- Status Info -->
        <div v-if="isLoading" class="alert alert-info">
          <i class="bi bi-arrow-repeat me-2"></i> Loading logs...
        </div>
        <div v-else-if="error" class="alert alert-danger">
          <i class="bi bi-exclamation-triangle me-2"></i> {{ error }}
        </div>

        <!-- Logs Container -->
        <div v-else>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <small class="text-muted">Показано {{ logs.length }} логов</small>
            <button
                class="btn btn-sm btn-outline-secondary"
                @click="exportLogs"
            >
              <i class="bi bi-download me-1"></i> Экспорт
            </button>
          </div>

          <div class="table-responsive">
            <table class="table table-sm table-hover">
              <thead>
              <tr>
                <th width="100px">Тип</th>
                <th width="160px">Время</th>
                <th width="120px" v-if="hasContext">Контекст</th>
                <th>Сообщение</th>
              </tr>
              </thead>
              <tbody>
              <tr
                  v-for="(log, index) in logs"
                  :key="index"
                  @click="showLogDetails(log)"
                  style="cursor: pointer;"
                  :class="`table-${getLogLevelClass(log.level)}`"
              >
                <td>
                    <span class="badge" :class="`bg-${getLogLevelClass(log.level)}`">
                      {{ log.level }}
                    </span>
                </td>
                <td>{{ formatTimestamp(log.timestamp) }}</td>
                <td v-if="hasContext">
                    <span class="badge bg-light text-dark" v-if="log.context">
                      {{ log.context }}
                    </span>
                </td>
                <td>
                  <div class="log-message text-truncate" style="max-width: 400px;">
                    {{ log.message }}
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Log Details Modal -->
    <div class="modal fade" id="logDetailsModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <span class="badge" :class="`bg-${getLogLevelClass(selectedLog.level)}`">
                {{ selectedLog.level }}
              </span>
              Log Details
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="row mb-3">
              <div class="col-md-6">
                <strong>Timestamp:</strong> {{ formatTimestamp(selectedLog.timestamp) }}
              </div>
              <div class="col-md-6" v-if="selectedLog.context">
                <strong>Context:</strong> {{ selectedLog.context }}
              </div>
            </div>

            <div class="mb-3">
              <strong>Message:</strong>
              <div class="p-2 bg-light rounded mt-1">
                <pre class="mb-0">{{ selectedLog.message }}</pre>
              </div>
            </div>

            <div v-if="selectedLog.trace">
              <strong>Stack Trace:</strong>
              <div class="p-2 bg-dark text-light rounded mt-1">
                <pre class="mb-0 text-white-50">{{ selectedLog.trace }}</pre>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useLogsStore } from '@/store/logs';
import { debounce } from 'lodash-es';
import { Modal } from 'bootstrap';

const logsStore = useLogsStore();
const logs = computed(() => logsStore.logs)
const availableDates = computed(() => logsStore.availableDates)
const isLoading = computed(() => logsStore.isLoading)
const error = computed(() => logsStore.error)
const filters = computed(() => logsStore.filters)

const selectedLog = ref({});
let logDetailsModal = null;

const hasContext = computed(() => {
  return logs.value.some(log => log.context);
});

const debouncedSearch = debounce(() => {
  logsStore.updateFilter('search', filters.value.search);
}, 500);

onMounted(() => {
  logsStore.fetchAvailableDates();
  logsStore.fetchLogs();
  logDetailsModal = new Modal(document.getElementById('logDetailsModal'));
});

function updateFilter(filter, value) {
  logsStore.updateFilter(filter, value);
}

function clearSearch() {
  filters.value.search = '';
  logsStore.updateFilter('search', '');
}

function fetchLogs() {
  logsStore.fetchLogs();
}

function getLogLevelClass(level) {
  switch (level) {
    case 'error': return 'danger';
    case 'warn': return 'warning';
    case 'info': return 'info';
    default: return 'light';
  }
}

function formatTimestamp(timestamp) {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleString();
}

function showLogDetails(log) {
  selectedLog.value = log;
  logDetailsModal.show();
}

function exportLogs() {
  const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(
      JSON.stringify(logs.value, null, 2)
  );
  const downloadAnchorNode = document.createElement('a');
  downloadAnchorNode.setAttribute("href", dataStr);
  downloadAnchorNode.setAttribute("download", `logs-${new Date().toISOString()}.json`);
  document.body.appendChild(downloadAnchorNode);
  downloadAnchorNode.click();
  downloadAnchorNode.remove();
}
</script>

<style scoped>
.log-message {
  font-family: monospace;
  white-space: pre-wrap;
}

.table-danger {
  --bs-table-bg: rgba(220, 53, 69, 0.05);
}

.table-warning {
  --bs-table-bg: rgba(255, 193, 7, 0.05);
}

.table-info {
  --bs-table-bg: rgba(13, 202, 240, 0.05);
}

.table-secondary {
  --bs-table-bg: rgba(108, 117, 125, 0.05);
}

pre {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
